import React, { useEffect, useMemo } from 'react';
import TaskManager from './TaskManager';
import SearchPaths from './SearchPaths';
import Actions from './Actions';
import Filters from './Filters';
import SelectInput from 'components/input/selectInput';
import {
  useImageManagementTaskIsDirty,
  useImageManagementTasks,
  useIsSelectedTaskRunning,
  useSelectedImageActionId,
  useSelectedImageManagementTask,
  useSelectedImageTaskId,
} from 'redux/hooks/imageManagementTask';
import {
  updateImageTaskResultsList,
  useOnChangeImageTaskValue,
  useFetchImageStorage,
  useOnStartImageTask,
  useOnUpdateSelectedImageActionId,
  useUpdateIsSelectedTaskRunning,
  useUpdateIsPreview,
  useUpdateIsError,
} from '../hooks';
import { onErrorTypeOptions } from '../constants';
import { ImageManagementActionDTO } from 'business/models/Models';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { FmlxButton, FmlxCheckBox } from 'fmlx-common-ui';
interface TaskOptionProps {
  setSelectedTab: React.Dispatch<React.SetStateAction<string>>;
}

const TaskOption = (props: TaskOptionProps) => {
  const { setSelectedTab } = props;
  const selectedImageTaskId = useSelectedImageTaskId();
  const imageTasks = useImageManagementTasks();
  const selectedActionId = useSelectedImageActionId();
  const updateSelectedImageActionId = useOnUpdateSelectedImageActionId();
  const onChangeImageTaskValue = useOnChangeImageTaskValue();
  const selectedTask = useSelectedImageManagementTask();
  const onUpdateImageTasksResultList = updateImageTaskResultsList();
  const onStartImageTask = useOnStartImageTask();
  const isDirty = useImageManagementTaskIsDirty();
  const isTaskSelected = selectedImageTaskId ? false : true;
  const isRunning = useIsSelectedTaskRunning();
  const taskStatus = isRunning ? 'Running' : 'Stopped';
  const setIsSelectedTaskRunning = useUpdateIsSelectedTaskRunning();
  const fetchImageStorage = useFetchImageStorage();
  const setIsPreview = useUpdateIsPreview();
  const setIsError = useUpdateIsError();

  useEffect(() => {
    fetchImageStorage();
  }, []);

  const onChangeErrorType = (e) => {
    onChangeImageTaskValue(
      'errorBehaviour',
      onErrorTypeOptions.find((err) => err.title === e.target.value)?.id,
    );
  };

  const selectedErrorBehaviour = useMemo(() => {
    const currentTask = imageTasks?.find((task) => task.id === selectedImageTaskId);
    return onErrorTypeOptions.find((opt) => opt.id === currentTask?.errorBehaviour)
      ?.title;
  }, [selectedImageTaskId, imageTasks]);

  const actions: ImageManagementActionDTO[] = useMemo(() => {
    if (!selectedImageTaskId) {
      return [];
    }

    const currentImageTaskActions = imageTasks?.find(
      (task) => task.id === selectedImageTaskId,
    )?.imageManagementActionList;

    if (
      currentImageTaskActions?.length &&
      !currentImageTaskActions?.find((act) => act.id === selectedActionId)
    ) {
      updateSelectedImageActionId(currentImageTaskActions[0]?.id || 0);
    }
    return currentImageTaskActions;
  }, [selectedImageTaskId, imageTasks]);

  const selectedAction: ImageManagementActionDTO = useMemo(() => {
    if (selectedActionId) {
      const currentAction = actions?.find((actn) => actn.id === selectedActionId);
      return currentAction;
    }
  }, [selectedActionId, actions]);

  const onChangeRunInBackground = () => {
    onChangeImageTaskValue('isBackground', !selectedTask?.isBackground);
  };

  const onClickPreview = async () => {
    onUpdateImageTasksResultList([]);
    setSelectedTab('Results');
    setIsPreview(true);
    setIsError(false);
    const selectedTaskTemp = { ...selectedTask };
    selectedTaskTemp.isPreview = true;
    await onStartImageTask(selectedTaskTemp);
  };

  const onClickStart = async () => {
    setSelectedTab('Results');
    setIsPreview(false);
    setIsError(false);
    const selectedTaskTemp = { ...selectedTask };
    selectedTaskTemp.isPreview = false;
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'Task has been Started.',
    );
    await onStartImageTask(selectedTaskTemp);
    setIsSelectedTaskRunning(true);
  };

  return (
    <div id="taskoption-wrapper" className="flex flex-column resultHeight">
      <TaskManager />
      <div
        id="taskoption-searchpaths-wrapper"
        className="flex flex-row h-[84%] border-t-0 border-l-0 border-r-0 border-b-[1px] border-solid border-[#E0E0E0]"
      >
        <div
          id="taskoption-searchpaths-topbar"
          className="flex flex-col w-[23%] h-[100%]"
        >
          <div
            id="taskoption-searchpaths-title"
            className="flex flex-col justify-center ml-[2%] h-[10%] text-[14px] font-bold"
            style={{ fontFamily: 'Roboto' }}
          >
            Search Paths
          </div>
          <div
            id="taskoption-searchpaths-wrapper"
            className="flex flex-col w-[98%] h-[90%] border border-gray-300 ml-[2%] mb-[1%]"
          >
            <SearchPaths />
          </div>
        </div>
        <div id="taskoption-actions-wrapper" className="w-[32%] h-[100%] ml-[4px]">
          <Actions />
        </div>
        <div id="taskoption-filters-wrapper" className="w-[46%] h-[100%]">
          <Filters setSelectedTab={setSelectedTab} />
        </div>
      </div>

      <div id="taskoption-bottombar" className="row h-10">
        <div
          id="taskoption-bottombar-wrapper"
          className="col-md-12 flex flex-row h-[100%] justify-end"
        >
          <div
            id="taskoption-bottombar-container"
            className="w-auto flex flex-row h-[100%] items-center gap-6"
          >
            <div
              id="taskoption-bottombar-onerror-wrapper"
              className="flex flex-row items-center w-[55%] h-[100%] "
            >
              <div
                id="taskoption-bottombar-onerror-title"
                className="text-[14px] h-[100%] flex items-center w-auto whitespace-nowrap"
                style={{ fontFamily: 'Roboto' }}
              >
                On Error
              </div>
              <div
                id="taskoption-bottombar-onerror-selectinput"
                className="flex items-center h-[100%] w-[100%] ml-[2%]"
              >
                <SelectInput
                  className="w-[100%] h-[75%]"
                  options={onErrorTypeOptions}
                  value={selectedErrorBehaviour}
                  onChange={onChangeErrorType}
                  isDisabled={!selectedAction}
                />
              </div>
            </div>

            <div
              id="taskoption-bottombar-runinbackground"
              className="h-7 items-center w-auto whitespace-nowrap"
            >
              <FmlxCheckBox
                id="taskoption-bottombar-runinbackground-checkbox"
                label={'Run in Background'}
                onChange={onChangeRunInBackground}
                checked={selectedTask?.isBackground}
              />
            </div>
            <div
              id="taskoption-bottombar-button-container"
              className="flex items-center gap-2"
            >
              <FmlxButton
                id="taskoption-bottombar-preview-button"
                label={'PREVIEW'}
                type={FmlxButton.Type.PRIMARY}
                variant={FmlxButton.Variant.OUTLINE}
                onClick={onClickPreview}
                disabled={
                  isDirty ||
                  selectedTask?.isBackground ||
                  isTaskSelected ||
                  isRunning ||
                  selectedTask?.imageManagementActionList?.length === 0
                }
              />
              <FmlxButton
                id="taskoption-bottombar-start-button"
                label={'START'}
                type={FmlxButton.Type.PRIMARY}
                onClick={onClickStart}
                disabled={
                  isDirty ||
                  selectedTask?.isBackground ||
                  isTaskSelected ||
                  isRunning ||
                  !(selectedTask?.imageManagementActionList?.length >= 1)
                }
              />
            </div>

            <div
              id="taskoption-bottombar-status-wrapper"
              className=" flex flex-row w-[20%] h-[100%] items-center"
              style={{ fontFamily: 'Roboto' }}
            >
              <div
                id="taskoption-bottombar-status-title"
                className="text-[14px] ml-[1%] font-bold"
              >
                Status:
              </div>

              <div
                id="taskoption-bottombar-status-task"
                className="text-[14px] ml-[1%] font-bold"
              >
                {taskStatus}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskOption;
